import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ActionIcon,
  Button,
  Center,
  Flex,
  Loader,
  Modal,
  Select,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from "mantine-react-table";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useDebouncedValue } from "@mantine/hooks";
import { COUNTRIES_FILTER_DATA } from "@/components/shared/constants/general.const";

type RegistryRow = {
  id: string;
  headquartersCountry: string;
  registrationNumberNormalized: string;
  companyName: string;
  companyAddress: string;
  dateOfIncorporation: string;
  companyType: string;
  registryCompanyStatus: string;
  verificationSource: string;
  createdAt: Date;
  updatedAt: Date;
};

const PAGE_SIZE = 15;

/** Dedupes email-link prefill when React Strict Mode runs effects twice (same mount query). */
const appliedIndirectEmailPrefillQueries = new Set<string>();

const INDIRECT_REGISTRY_COMPANY_STATUS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
] as const;

function indirectRegistryStatusForForm(stored: string): string {
  const t = stored.trim().toLowerCase();
  if (t === "active") return "Active";
  if (t === "inactive") return "Inactive";
  return "";
}

const emptyForm = {
  id: "" as string | undefined,
  headquartersCountry: "",
  registrationNumber: "",
  companyName: "",
  companyAddress: "",
  dateOfIncorporation: "",
  companyType: "",
  registryCompanyStatus: "",
  verificationSource: "",
};

export default function AdminIndirectVerificationPage() {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<RegistryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const previousParamsRef = useRef<string>("");

  useEffect(() => {
    const fingerprint = searchParams.toString();
    if (!fingerprint || appliedIndirectEmailPrefillQueries.has(fingerprint)) return;

    const country = searchParams.get("country")?.trim();
    const reg = searchParams.get("reg")?.trim();
    const name = searchParams.get("name")?.trim();
    const gtid = searchParams.get("gtid")?.trim();
    const userEmail = searchParams.get("userEmail")?.trim();
    if (!country && !reg && !name && !gtid && !userEmail) return;

    appliedIndirectEmailPrefillQueries.add(fingerprint);

    const sourceParts: string[] = [];
    if (gtid) sourceParts.push(`GTID ${gtid}`);
    if (userEmail) sourceParts.push(`user ${userEmail}`);
    const verificationSource =
      sourceParts.length > 0 ? `Client verification request — ${sourceParts.join(", ")}` : "";

    setForm({
      ...emptyForm,
      id: undefined,
      headquartersCountry: country ?? "",
      registrationNumber: reg ?? "",
      companyName: name ?? "",
      verificationSource,
    });
    setModalOpen(true);
    notifications.show({
      title: "Client details loaded",
      message: "Fields from the email link were filled in. Complete the remaining registry fields and save.",
      color: "blue",
    });
  }, [searchParams]);

  const countryOptions = useMemo(
    () =>
      COUNTRIES_FILTER_DATA.flatMap((g) =>
        g.items.map((item) => ({ value: item.value, label: item.label }))
      ),
    []
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trpc.admin.indirectVerificationList.query({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedSearch.trim() || undefined,
      });
      setRows(
        res.rows.map((r) => ({
          ...r,
          createdAt: r.createdAt instanceof Date ? r.createdAt : new Date(r.createdAt as unknown as string),
          updatedAt: r.updatedAt instanceof Date ? r.updatedAt : new Date(r.updatedAt as unknown as string),
        }))
      );
      setTotalRows(res.total);
    } catch (err) {
      handleTrpcError(err);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    const paramsString = JSON.stringify({
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      search: debouncedSearch,
    });
    if (paramsString === previousParamsRef.current) {
      return;
    }
    previousParamsRef.current = paramsString;
    void fetchRows();
  }, [fetchRows, pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
  }, [debouncedSearch]);

  const openCreate = () => {
    setForm({ ...emptyForm, id: undefined });
    setModalOpen(true);
  };

  const openEdit = (row: RegistryRow) => {
    setForm({
      id: row.id,
      headquartersCountry: row.headquartersCountry,
      registrationNumber: row.registrationNumberNormalized,
      companyName: row.companyName,
      companyAddress: row.companyAddress ?? "",
      dateOfIncorporation: row.dateOfIncorporation ?? "",
      companyType: row.companyType,
      registryCompanyStatus: indirectRegistryStatusForForm(row.registryCompanyStatus),
      verificationSource: row.verificationSource,
    });
    setModalOpen(true);
  };

  const save = async () => {
    const missing: string[] = [];
    if (!form.headquartersCountry.trim()) missing.push("headquarters country");
    if (!form.registrationNumber.trim()) missing.push("registration number");
    if (!form.companyName.trim()) missing.push("company name");
    if (!form.companyAddress.trim()) missing.push("company address");
    if (!form.dateOfIncorporation.trim()) missing.push("date of incorporation");
    if (!form.companyType.trim()) missing.push("company type");
    if (!form.registryCompanyStatus.trim()) missing.push("company status");
    if (missing.length > 0) {
      notifications.show({
        title: "Missing fields",
        message: `Required: ${missing.join(", ")}.`,
        color: "red",
      });
      return;
    }
    setSaving(true);
    try {
      await trpc.admin.indirectVerificationUpsert.mutate({
        id: form.id,
        headquartersCountry: form.headquartersCountry.trim(),
        registrationNumber: form.registrationNumber.trim(),
        companyName: form.companyName.trim(),
        companyAddress: form.companyAddress.trim(),
        dateOfIncorporation: form.dateOfIncorporation.trim(),
        companyType: form.companyType.trim(),
        registryCompanyStatus: form.registryCompanyStatus.trim(),
        verificationSource: form.verificationSource.trim(),
      });
      notifications.show({
        title: form.id ? "Updated" : "Created",
        message: "Indirect registry row saved.",
        color: "green",
      });
      setModalOpen(false);
      setForm(emptyForm);
      await fetchRows();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: RegistryRow) => {
    if (!window.confirm(`Delete registry row for ${row.companyName}?`)) return;
    try {
      await trpc.admin.indirectVerificationDelete.mutate({ id: row.id });
      notifications.show({ title: "Deleted", message: "Row removed.", color: "green" });
      await fetchRows();
    } catch (err) {
      handleTrpcError(err);
    }
  };

  const columns = useMemo<MRT_ColumnDef<RegistryRow>[]>(
    () => [
      { accessorKey: "headquartersCountry", header: "Country" },
      { accessorKey: "registrationNumberNormalized", header: "Reg. # (normalized)" },
      { accessorKey: "companyName", header: "Company name" },
      {
        accessorKey: "companyAddress",
        header: "Address",
        Cell: ({ cell }) => (
          <Text size="sm" lineClamp={2} maw={220}>
            {String(cell.getValue() ?? "")}
          </Text>
        ),
      },
      { accessorKey: "dateOfIncorporation", header: "Incorp. date" },
      { accessorKey: "companyType", header: "Company type" },
      { accessorKey: "registryCompanyStatus", header: "Registry status" },
      { accessorKey: "verificationSource", header: "Source" },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => (
          <Flex gap={4}>
            <Tooltip label="Edit">
              <ActionIcon variant="subtle" color="primary" onClick={() => openEdit(row.original)}>
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Delete">
              <ActionIcon variant="subtle" color="red" onClick={() => void remove(row.original)}>
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        ),
      },
    ],
    // openEdit/remove stable enough for column defs
    []
  );

  return (
    <Flex direction="column" gap="md" p="md">
      <Flex justify="space-between" align="center" wrap="wrap" gap="sm">
        <div>
          <Title order={2} fw={500}>
            Indirect verification registry
          </Title>
        </div>
        <Button leftSection={<IconPlus size={18} />} onClick={openCreate}>
          Add row
        </Button>
      </Flex>

      <TextInput
        placeholder="Search country, registration, company name…"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        maw={400}
      />

      <MantineReactTable
        columns={columns}
        data={rows}
        manualPagination
        rowCount={totalRows}
        state={{ pagination, isLoading: loading }}
        onPaginationChange={setPagination}
        enableGlobalFilter={false}
        enableColumnFilters={false}
        enableColumnActions={false}
        mantineTableContainerProps={{ style: { minHeight: 400 } }}
        mantineTableProps={{ highlightOnHover: true, withTableBorder: true }}
      />

      {loading && rows.length === 0 && (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={form.id ? "Edit registry row" : "Add registry row"}
        size="lg"
      >
        <Flex direction="column" gap="sm">
          <Select
            label="Headquarters country"
            placeholder="Select country"
            searchable
            data={countryOptions}
            value={form.headquartersCountry || null}
            onChange={(v) => setForm((f) => ({ ...f, headquartersCountry: v ?? "" }))}
            required
          />
          <TextInput
            label="Registration number"
            value={form.registrationNumber}
            onChange={(e) => setForm((f) => ({ ...f, registrationNumber: e.currentTarget.value }))}
            required
          />
          <TextInput
            label="Company name"
           value={form.companyName}
            onChange={(e) => setForm((f) => ({ ...f, companyName: e.currentTarget.value }))}
            required
          />
          <Textarea
            label="Company address"
            autosize
            minRows={2}
            value={form.companyAddress}
            onChange={(e) => setForm((f) => ({ ...f, companyAddress: e.currentTarget.value }))}
            required
          />
          <TextInput
            label="Date of incorporation"
            description="As shown on the certificate (any format the client should see)."
            value={form.dateOfIncorporation}
            onChange={(e) => setForm((f) => ({ ...f, dateOfIncorporation: e.currentTarget.value }))}
            required
          />
          <TextInput
            label="Company type"
            value={form.companyType}
            onChange={(e) => setForm((f) => ({ ...f, companyType: e.currentTarget.value }))}
            required
          />
          <Select
            label="Registry company status"
            placeholder="Select status"
            data={[...INDIRECT_REGISTRY_COMPANY_STATUS]}
            value={form.registryCompanyStatus || null}
            onChange={(v) => setForm((f) => ({ ...f, registryCompanyStatus: v ?? "" }))}
            required
          />
          <TextInput
            label="Verification source"
            placeholder="e.g. Manual admin, Registry export 2026"
            value={form.verificationSource}
            onChange={(e) => setForm((f) => ({ ...f, verificationSource: e.currentTarget.value }))}
          />
          <Button loading={saving} onClick={() => void save()}>
            Save
          </Button>
        </Flex>
      </Modal>
    </Flex>
  );
}
